import { getSignedUrl } from "../../../../graphQL/query/file";
import { saveBrandKitAsset, BrandKitAssetType } from "../../../../graphQL/mutations/brandKit";
import { authorization } from "../../../../module/auth";

// ─── Constants ────────────────────────────────────────────────────────────────

const BUCKET = "oppizi-uploads";
const REGION = "ap-southeast-2";
const ACL = "public-read";
const S3_BASE_URL = `https://${BUCKET}.s3.${REGION}.amazonaws.com`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const sanitizeName = (name: string): string => encodeURIComponent(name).replace(/[^a-zA-Z0-9]/g, "");

/**
 * Resolves the signed URL string from the various response shapes
 * that getSignedUrl can return.
 */
const resolveSignedUrl = (response: unknown): string => {
  if (typeof response === "string") return response;
  if (response && typeof response === "object") {
    const r = response as any;
    if (typeof r.url === "string") return r.url;
    if (typeof r.data?.getSignedUrl === "string") return r.data.getSignedUrl;
  }
  throw new Error(`Unexpected signed URL response shape: ${JSON.stringify(response)}`);
};

// ─── Upload ───────────────────────────────────────────────────────────────────

export interface BrandKitUploadResult {
  name: string;
  s3Url: string;
  s3Key: string;
  mimeType: string;
  fileSize: string;
  type: BrandKitAssetType;
}

/**
 * Upload a file to S3 as a brand kit asset and persist metadata via the API.
 *
 * @param file      The file to upload
 * @param type      Asset type: logo | image | font | icon
 * @param name      Optional display name (defaults to filename without extension)
 * @returns         Uploaded asset metadata
 */
export const uploadBrandKitAsset = async (
  file: File,
  type: BrandKitAssetType,
  name?: string
): Promise<BrandKitUploadResult> => {
  const user = authorization.getUser();
  if (!user?.clientId) throw new Error("User not authenticated or missing clientId");

  const assetName = name || file.name.replace(/\.[^.]+$/, "");
  const timestamp = Date.now();
  const sanitized = sanitizeName(assetName);
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  const typeFolder = `${type}s`; // logo→logos, image→images, font→fonts, icon→icons
  const s3Key = `campaign-builder/brand-kit/${user.clientId}/${typeFolder}/${sanitized}-${timestamp}.${ext}`;

  // 1. Get signed URL for upload
  const signedUrlResponse = await getSignedUrl(BUCKET, s3Key, "PUT", REGION, ACL);

  if (signedUrlResponse instanceof Error) {
    throw new Error(`Failed to get signed URL: ${signedUrlResponse.message}`);
  }

  const signedUrl = resolveSignedUrl(signedUrlResponse);

  if (!signedUrl.startsWith("http")) {
    throw new Error(`Invalid signed URL format: ${signedUrl}`);
  }

  // 2. Upload file to S3
  const uploadResponse = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
  }

  const s3Url = `${S3_BASE_URL}/${s3Key}`;

  // 3. Persist asset metadata via the API
  const res = await saveBrandKitAsset({
    type,
    name: assetName,
    s3Url,
    s3Key,
    mimeType: file.type,
    fileSize: String(file.size),
  });

  const json = await (res as unknown as Response).json();
  const result = json?.data?.saveBrandKitAsset;

  if (!result?.success) {
    throw new Error(result?.error || "Failed to save brand kit asset");
  }

  return { name: assetName, s3Url, s3Key, mimeType: file.type, fileSize: String(file.size), type };
};
